import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';
import {
  AccordionStyle,
  FaqBta
} from '../../../../constants';
import {
  Accordion,
  FaqComponent
} from '../../../../components';
import { BTAAccountSummary } from '../../../../components';
import { useRecoilValue } from 'recoil';
import {
  btaTransactionHistory,
  useIsMobile
} from '../../../../recoil';
import { ArrowDown } from '../../../../assets';
import { formatCurrency } from '../../../../helpers';
import { Table } from '../../../../components/table/table';
import { useTealium } from '../../../../tealium/useTealium';
import { StatusChip } from '../../../../components/statusChip/statusChip';
import { BTAAdvanceBanner } from '../../../../components/BTAAdvanceBanner/BTAAdvanceBanner';
import styles from './btaAccount.module.css';

export const HeaderValues = {
  displayDate: 'Date',
  capital: 'Capital',
  cost: 'Cost',
  paymentAmount: 'Amount',
  repaymentState: 'Status'
};

export const BTAAccount = ({ applicationId, applicationStatus, businessId }) => {

  const [openSection, setOpenSection] = useState('summary');
  const transactionHistoryDetails = useRecoilValue(btaTransactionHistory(applicationId));
  const isMobile = useRecoilValue(useIsMobile);
  const isDataPresent = Array.isArray(transactionHistoryDetails.records) && transactionHistoryDetails.records.length > 0;
  const { track, events } = useTealium();

  useEffect(() => {
    track(events.businessTermAdvance.pageView());
  }, []);

  const transactionSection = () => {
    track(events.businessTermAdvance.btaAccordion('transaction history', openSection === 'history' ? 'collapsed': 'expanded'));
    setOpenSection(openSection === 'history' ? '' : 'history');
  };

  const summarySection = () => {
    track(events.businessTermAdvance.btaAccordion('account summary', openSection === 'summary' ? 'collapsed': 'expanded'));
    setOpenSection(openSection === 'summary' ? '' : 'summary');
  };

  return (
    <div className={styles.container}>
      <BTAAdvanceBanner
        applicationId={applicationId}
        applicationStatus={applicationStatus}
        businessId={businessId}
      />

      <div className={isMobile ? styles.titleContainerMobile : styles.titleContainer}>
        <div className={isMobile ? styles.titleHeadingMobile : styles.titleHeading}>
          Fixed Term Funding
        </div>
        <StatusChip status={applicationStatus.toLowerCase()} />
      </div>

      {/* Account Summary*/}
      <div className={styles.summaryAccordion}>
        <Accordion
          headerStyle={{ width: '95%' }}
          isOpen={openSection === 'summary'}
          headerChildren={
            <>
              <div style={AccordionStyle.accordionHeaderLeft}>
                <div className={isMobile ? styles.accordionTitleMobile : styles.accordionTitle}>
                  {'Account Summary'}
                </div>
              </div>

              <div>
                <img
                  style={{ ...AccordionStyle.ArrowImg, rotate: openSection === 'summary' ? '180deg' : '' }}
                  src={ArrowDown}
                  alt='DropdownOpen'
                  role='presentation'
                  onClick={summarySection}
                  onKeyDown={summarySection}
                />
              </div>
            </>
          }
          containerStyle={isMobile ? { borderRadius: 20, border: '1px solid #C9CCD4', paddingTop: 20, paddingBottom: 20, marginBottom: 30 } : { borderRadius: 5, border: '1px solid #C9CCD4', paddingTop: 20, paddingBottom: 20, marginBottom: 30 }}
        >
          <div style={AccordionStyle.accordionChildren}>
            <BTAAccountSummary
              applicationId={applicationId}
            />
          </div>
        </Accordion>
      </div>

      {/* Transaction History*/}
      <div className={styles.transactionHistoryAccordion}>
        <Accordion
          containerStyle={isMobile ? { borderRadius: 20, border: '1px solid #C9CCD4', paddingTop: 20, paddingBottom: 20, marginBottom: 30 } : { borderRadius: 5, border: '1px solid #C9CCD4', paddingTop: 20, paddingBottom: 20, marginBottom: 30 }}
          headerStyle={{ width: '95%' }}
          isOpen={openSection === 'history'}
          headerChildren={
            <>
              <div style={AccordionStyle.accordionHeaderLeft}>
                <div className={isMobile ? styles.accordionTitleMobile : styles.accordionTitle}>
                  {'Transaction history'}
                </div>
              </div>

              <div>
                <img
                  style={{ ...AccordionStyle.ArrowImg, rotate: openSection === 'history' ? '180deg' : '' }}
                  src={ArrowDown}
                  alt='DropdownOpen'
                  role='presentation'
                  onClick={transactionSection}
                  onKeyDown={transactionSection}
                />
              </div>
            </>
          }
        >
          <div style={AccordionStyle.accordionChildren}>
            <div className={styles.tableContainer}>
              {isDataPresent &&
                <Table
                  data={
                    transactionHistoryDetails.records.map((d) => ({
                      ...d,
                      cost: formatCurrency(d.cost),
                      capital: formatCurrency(d.capital),
                      paymentAmount: formatCurrency(d.paymentAmount),
                      paidAmount: formatCurrency(d.paidAmount),
                      arrears: formatCurrency(d.arrears),
                      earlySettlement: formatCurrency(d.earlySettlement),
                      paidDate: d.paidDate
                    }))
                  }
                  heading={HeaderValues}
                  footer={{
                    cost: formatCurrency(transactionHistoryDetails.total.cost),
                    capital: formatCurrency(transactionHistoryDetails.total.capital),
                    paymentAmount: formatCurrency(transactionHistoryDetails.total.paymentAmount),
                    paidAmount: formatCurrency(transactionHistoryDetails.total.paidAmount),
                    arrears: formatCurrency(transactionHistoryDetails.total.arrears),
                    earlySettlement: formatCurrency(transactionHistoryDetails.total.earlySettlement),
                    displayDate: 'TOTAL'
                  }}
                />
              }
              {!isDataPresent &&
                'No Transaction History Found'
              }
            </div>
          </div>
        </Accordion>
      </div>

      {/* FAQ */}
      <div className={isMobile ? styles.faqContainerOuterMobile : styles.faqContainerOuter}>
        <div className={styles.faqContainerInner}>
          <div className={styles.faqHeading}>
            FAQ&apos;s
          </div>
          {FaqBta.map((item) => {
            const key = `faq ${item.title?.replace('?', '')}`;
            return (<FaqComponent
              key={key}
              title={item.title}
              content={item.content}
              changeVisibility={() => {track(events.businessTermAdvance.btaAccordion(key, openSection === key ? 'collapsed': 'expanded')); setOpenSection(openSection === key ? '' : key);}}
              isOpen={openSection === key}
            />);
          })}
        </div>
      </div>
    </div>
  );
};

BTAAccount.propTypes = {
  applicationId: PropTypes.string.isRequired,
  applicationStatus: PropTypes.string.isRequired,
  businessId: PropTypes.string.isRequired
};
